/**
 * Helpers for resolving which Menu Header Code slot applies to a given page.
 *
 * A "menu header code" is an image/HTML banner rendered either above the header
 * or below the breaking-news bar. The CMS (Page Builder) owns the data; this app
 * syncs it into the local store and resolves the right slot per request/device.
 *
 * Pure functions only (no Node/`fs`/`headers`) so they run on both the server
 * (SSR) and the client (SPA navigation re-resolution).
 */

import {
  detectPageType,
  resolvePageId,
  normalizePath,
  matchUrlPattern,
  type PageType,
} from './layout-codes-helpers';
import type {
  MenuHeaderCodeConfig,
  MenuHeaderSlot,
  MenuHeaderPosition,
} from './local-store/types';

export type { PageType };
export { detectPageType, resolvePageId, normalizePath, matchUrlPattern };

/** A resolved banner ready to render. */
export interface ActiveMenuHeaderBanner {
  slug: string;
  position: MenuHeaderPosition;
  slot: MenuHeaderSlot;
}

/** A slot is renderable only if it actually carries content. */
export function slotHasContent(slot: MenuHeaderSlot | null | undefined): slot is MenuHeaderSlot {
  if (!slot) return false;
  if (slot.type === 'image') return !!slot.imageUrl;
  if (slot.type === 'html') return !!(slot.html && slot.html.trim());
  return false;
}

/** Pick mobile slot when the entry opts into separate mobile and we're on mobile. */
function pickSlot(
  desktop: MenuHeaderSlot | null | undefined,
  mobile: MenuHeaderSlot | null | undefined,
  hasSeparateMobile: boolean,
  isMobile: boolean
): MenuHeaderSlot | null {
  if (hasSeparateMobile && isMobile && slotHasContent(mobile)) return mobile!;
  return slotHasContent(desktop) ? desktop! : null;
}

export interface ResolveContext {
  pathname: string;
  pageType: PageType;
  pageId: string | null;
  isMobile: boolean;
}

/**
 * Resolve the slot a single Menu Header Code contributes for the current page,
 * honoring its scope and the device. Returns null when nothing applies.
 */
export function resolveSlotForCode(
  code: MenuHeaderCodeConfig,
  ctx: ResolveContext
): MenuHeaderSlot | null {
  if (code.isActive === false) return null;
  const hasMobile = code.hasSeparateMobile === true;

  switch (code.scope) {
    case 'all_pages':
      return pickSlot(code.content, code.mobileContent, hasMobile, ctx.isMobile);

    case 'page_based': {
      const desktop =
        ctx.pageType === 'home' ? code.homepageContent
          : ctx.pageType === 'section' ? code.sectionPageContent
            : code.articlePageContent;
      const mobile =
        ctx.pageType === 'home' ? code.mobileHomepageContent
          : ctx.pageType === 'section' ? code.mobileSectionPageContent
            : code.mobileArticlePageContent;
      return pickSlot(desktop, mobile, hasMobile, ctx.isMobile);
    }

    case 'specific_page': {
      const entries = code.specificPageCodes || [];
      const normPath = normalizePath(ctx.pathname);
      for (const entry of entries) {
        const matches =
          (!!ctx.pageId && !!entry.pageId && entry.pageId === ctx.pageId) ||
          (!!entry.pageSlug && (normPath === normalizePath(`/${entry.pageSlug}`) ||
            normPath.startsWith(normalizePath(`/${entry.pageSlug}`) + '/')));
        if (matches) {
          return pickSlot(entry.content, entry.mobileContent, hasMobile, ctx.isMobile);
        }
      }
      return null;
    }

    case 'url_based': {
      const entries = code.urlBasedCodes || [];
      for (const entry of entries) {
        if (entry.urlPattern && matchUrlPattern(entry.urlPattern, ctx.pathname)) {
          return pickSlot(entry.content, entry.mobileContent, hasMobile, ctx.isMobile);
        }
      }
      return null;
    }

    default:
      return null;
  }
}

/**
 * Resolve every active banner for a given position on the current page.
 * Entries are returned in slug order for stable rendering.
 */
export function getActiveMenuHeaderBanners(
  allCodes: Record<string, MenuHeaderCodeConfig>,
  pathname: string,
  isMobile: boolean,
  routePageIds: Record<string, string>,
  position: MenuHeaderPosition
): ActiveMenuHeaderBanner[] {
  const ctx: ResolveContext = {
    pathname: normalizePath(pathname),
    pageType: detectPageType(normalizePath(pathname)),
    pageId: resolvePageId(normalizePath(pathname), routePageIds),
    isMobile,
  };

  const out: ActiveMenuHeaderBanner[] = [];
  for (const [slug, code] of Object.entries(allCodes)) {
    if (code.isActive === false) continue;
    if ((code.position || 'below_breaking_news') !== position) continue;
    const slot = resolveSlotForCode(code, ctx);
    if (slotHasContent(slot)) {
      out.push({ slug, position, slot: slot! });
    }
  }
  return out.sort((a, b) => a.slug.localeCompare(b.slug));
}
